import { useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import {
  Eye, GitCompare, BarChart3, Zap, BookOpen, Wrench, ChevronDown, ChevronUp,
  Star, Users, TrendingUp, CheckCircle, ArrowRight, Globe, Shield, Clock
} from "lucide-react";

const ADDONS = [
  {
    id: "brand-monitor",
    icon: Eye,
    color: "purple",
    price: "€19",
    period: "mo",
    href: "/brand-monitor",
    tKey: "ao_bm",
  },
  {
    id: "competitor-spy",
    icon: GitCompare,
    color: "blue",
    price: "€29",
    period: "mo",
    href: "/products/competitor-spy",
    tKey: "ao_cs",
  },
  {
    id: "attribution",
    icon: BarChart3,
    color: "amber",
    price: "€39",
    period: "mo",
    href: "/attribution",
    tKey: "ao_at",
  },
  {
    id: "autopilot",
    icon: Zap,
    color: "emerald",
    price: "€79",
    period: "mo",
    href: "/products/seo-autopilot",
    tKey: "ao_ap",
  },
  {
    id: "blog-agent",
    icon: BookOpen,
    color: "rose",
    price: "€25",
    period: null,
    href: "/products/blog-agent",
    tKey: "ao_ba",
  },
  {
    id: "fix-seo",
    icon: Wrench,
    color: "orange",
    price: "€9",
    period: null,
    href: "/fix-my-seo",
    tKey: "ao_fx",
  },
  {
    id: "ads-creator",
    icon: Zap,
    color: "purple",
    price: "€69",
    period: "mo",
    href: null,
    tKey: "ao_ac",
    comingSoon: true,
  },
];

const COLORS = {
  purple: { bg: "bg-purple-50", border: "border-purple-200", icon: "text-purple-600", badge: "bg-purple-100 text-purple-700", hover: "hover:border-purple-400" },
  blue: { bg: "bg-blue-50", border: "border-blue-200", icon: "text-blue-600", badge: "bg-blue-100 text-blue-700", hover: "hover:border-blue-400" },
  amber: { bg: "bg-amber-50", border: "border-amber-200", icon: "text-amber-600", badge: "bg-amber-100 text-amber-700", hover: "hover:border-amber-400" },
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", icon: "text-emerald-600", badge: "bg-emerald-100 text-emerald-700", hover: "hover:border-emerald-400" },
  rose: { bg: "bg-rose-50", border: "border-rose-200", icon: "text-rose-600", badge: "bg-rose-100 text-rose-700", hover: "hover:border-rose-400" },
  orange: { bg: "bg-orange-50", border: "border-orange-200", icon: "text-orange-600", badge: "bg-orange-100 text-orange-700", hover: "hover:border-orange-400" },
};

export default function AddonsTab() {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(null);

  const toggle = (id) => setExpanded(expanded === id ? null : id);

  return (
    <div className="space-y-6" data-testid="addons-tab">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-black font-['Chivo']">{t.ao_title || "Add-ons & Tools"}</h2>
        <p className="text-sm text-gray-500 mt-1">{t.ao_subtitle || "Extend your TopRankPulse with powerful AI tools. Each add-on works independently — activate only what you need."}</p>
      </div>

      {/* Add-on Cards */}
      <div className="space-y-3">
        {ADDONS.map((addon) => {
          const c = COLORS[addon.color];
          const isOpen = expanded === addon.id;
          const name = t[`${addon.tKey}_name`] || addon.id;
          const short = t[`${addon.tKey}_short`] || "";
          const detail = t[`${addon.tKey}_detail`] || "";
          const steps = [
            t[`${addon.tKey}_s1`], t[`${addon.tKey}_s2`], t[`${addon.tKey}_s3`]
          ].filter(Boolean);
          const demo = t[`${addon.tKey}_demo`] || "";
          const ref1 = t[`${addon.tKey}_ref`] || "";
          const benefit = t[`${addon.tKey}_benefit`] || "";
          const risk = t[`${addon.tKey}_risk`] || "";

          return (
            <div key={addon.id} className={`bg-white border ${c.border} ${c.hover} transition-all`} data-testid={`addon-${addon.id}`}>
              {/* Collapsed view */}
              <button
                onClick={() => toggle(addon.id)}
                className="w-full flex items-center gap-4 p-4 text-left"
                data-testid={`addon-toggle-${addon.id}`}
              >
                <div className={`w-10 h-10 ${c.bg} flex items-center justify-center flex-shrink-0`}>
                  <addon.icon className={`w-5 h-5 ${c.icon}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-sm text-gray-900">{name}</span>
                    <span className={`text-[10px] px-2 py-0.5 font-bold ${c.badge}`}>
                      {addon.price}{addon.period ? `/${t.ao_month || addon.period}` : ` ${t.ao_onetime || "one-time"}`}
                    </span>
                    {addon.comingSoon && (
                      <span className="text-[10px] px-2 py-0.5 font-bold bg-gray-200 text-gray-600 uppercase">{t.ao_coming || "Coming Soon"}</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{short}</p>
                </div>
                {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
              </button>

              {/* Expanded detail */}
              {isOpen && (
                <div className="px-4 pb-5 border-t border-gray-100 pt-4 space-y-4" data-testid={`addon-detail-${addon.id}`}>
                  {/* Description */}
                  <p className="text-sm text-gray-700 leading-relaxed">{detail}</p>

                  {/* Benefit / Risk for regular people */}
                  {(benefit || risk) && (
                    <div className="grid sm:grid-cols-2 gap-3">
                      {benefit && (
                        <div className="bg-emerald-50 border border-emerald-200 p-3" data-testid={`addon-benefit-${addon.id}`}>
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                            <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider">{t.ao_if_order || "If you order"}</p>
                          </div>
                          <p className="text-sm text-emerald-900 leading-relaxed">{benefit}</p>
                        </div>
                      )}
                      {risk && (
                        <div className="bg-red-50 border border-red-200 p-3" data-testid={`addon-risk-${addon.id}`}>
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <Shield className="w-3.5 h-3.5 text-red-600" />
                            <p className="text-xs font-bold text-red-700 uppercase tracking-wider">{t.ao_if_not || "If you don't"}</p>
                          </div>
                          <p className="text-sm text-red-900 leading-relaxed">{risk}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* How to use */}
                  {steps.length > 0 && (
                    <div>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{t.ao_how || "How it works"}</p>
                      <div className="space-y-2">
                        {steps.map((step, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <span className={`w-5 h-5 ${c.bg} flex items-center justify-center text-[10px] font-bold ${c.icon} flex-shrink-0 mt-0.5`}>{i + 1}</span>
                            <p className="text-sm text-gray-600">{step}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Demo data */}
                  {demo && (
                    <div className={`${c.bg} border ${c.border} p-3`}>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">{t.ao_sample || "Sample results"}</p>
                      <p className="text-sm text-gray-700">{demo}</p>
                    </div>
                  )}

                  {/* Reference */}
                  {ref1 && (
                    <div className="flex items-start gap-2 p-3 bg-gray-50 border border-gray-100">
                      <Star className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-gray-600 italic">"{ref1}"</p>
                    </div>
                  )}

                  {/* CTA */}
                  <a
                    href={addon.href}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white text-sm font-bold hover:bg-gray-800 transition-colors"
                    data-testid={`addon-cta-${addon.id}`}
                  >
                    {t.ao_try || "Try it now"} <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom info */}
      <div className="bg-gray-50 border border-gray-200 p-4 flex items-start gap-3">
        <Shield className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-gray-700">{t.ao_guarantee || "30-day money-back guarantee on all add-ons"}</p>
          <p className="text-xs text-gray-500 mt-0.5">{t.ao_cancel || "Cancel anytime. No contracts. Instant activation."}</p>
        </div>
      </div>
    </div>
  );
}
